import { useAuthStore } from '@/stores/useAuthStore'
import { useMutation } from '@tanstack/react-query'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { UserRole } from '../../users/types/user'
import { authApi } from '../api/authApi'
import { clearAuthSession, tokenStorage } from '../auth-session'
import { getSignInUrl } from '../return-url'
import type { AuthMe } from '../types/auth'

let meRequest: Promise<AuthMe> | null = null
let didRefreshMeOnAppLoad = false

export const useAuth = () => {
  const navigate = useNavigate()
  const currentUser = useAuthStore(s => s.currentUser)
  const isRefreshingMe = useAuthStore(s => s.isRefreshingMe)
  const setUser = useAuthStore(s => s.setCurrentUser)
  const setIsRefreshingMe = useAuthStore(s => s.setIsRefreshingMe)

  const refreshMe = useCallback(async () => {
    setIsRefreshingMe(true)
    try {
      const userData = await authApi.me()
      setUser(userData)
      return userData
    } finally {
      setIsRefreshingMe(false)
    }
  }, [setIsRefreshingMe, setUser])

  useEffect(() => {
    const token = tokenStorage.getToken()
    if (token && !didRefreshMeOnAppLoad) {
      didRefreshMeOnAppLoad = true
      meRequest = meRequest ?? refreshMe()
      meRequest
        .catch(() => {
          clearAuthSession()
        })
        .finally(() => {
          meRequest = null
        })
    }
  }, [refreshMe])

  const signinMutation = useMutation({
    mutationFn: async ({ username, password }: { username: string; password: string }) => {
      const token = await authApi.login(username, password)
      const userData = await authApi.me(token)
      return { token, userData }
    },
    onSuccess: ({ token, userData }) => {
      tokenStorage.setToken(token)
      setUser(userData)
      didRefreshMeOnAppLoad = true
    },
  })

  const signout = async () => {
    try {
      await authApi.logout()
    } finally {
      logout()
    }
  }

  const logout = () => {
    didRefreshMeOnAppLoad = false
    clearAuthSession()
    navigate(getSignInUrl(), { replace: true })
  }

  const isDepartmentHead = (departmentId?: string | null): boolean => {
    if (!departmentId || !currentUser) return false
    return currentUser.departments.some(d => d.id === departmentId && d.headId === currentUser.id)
  }

  const canManageDepartment = (sourceDepartmentId?: string | null): boolean => {
    return currentUser?.role === UserRole.Admin || isDepartmentHead(sourceDepartmentId)
  }

  const isDepartmentMember = (departmentId?: string | null): boolean => {
    if (!departmentId || !currentUser) return false
    return currentUser.role === UserRole.Admin || currentUser.departments.some(d => d.id === departmentId)
  }

  return {
    signin: signinMutation.mutateAsync,
    isLoading: signinMutation.isPending,
    error: signinMutation.error,
    signout,
    currentUser,
    refreshMe,
    departments: currentUser?.departments ?? [],
    primaryDepartment: currentUser?.primaryDepartment ?? null,
    primaryDepartmentId: currentUser?.primaryDepartmentId ?? null,
    canManageDepartment,
    isDepartmentMember,
    isRefreshingMe,
    isAdmin: currentUser?.role === UserRole.Admin,
    isMaintainer: currentUser?.role === UserRole.Maintainer,
    isVendor: currentUser?.role === UserRole.Vendor,
    isUser: currentUser?.role === UserRole.User,
  }
}
